import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { NavigateOptions, To } from 'react-router-dom';
import { addLocalePrefix, getLocaleFromPathname, stripLocalePrefix, type Locale } from './routing';

export function useLocale(): Locale {
  const location = useLocation();
  return getLocaleFromPathname(location.pathname);
}

export function useNormalizedPathname(): string {
  const location = useLocation();
  return stripLocalePrefix(location.pathname);
}

export function useLocalizedPath() {
  const locale = useLocale();
  return useCallback((to: string) => addLocalePrefix(to, locale), [locale]);
}

export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const locale = useLocale();

  return useCallback(
    (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to);
        return;
      }

      if (typeof to === 'string') {
        navigate(addLocalePrefix(to, locale), options);
        return;
      }

      const pathname = typeof to.pathname === 'string' ? addLocalePrefix(to.pathname, locale) : to.pathname;
      navigate({ ...to, pathname }, options);
    },
    [navigate, locale]
  );
}
